import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { List, X, Globe } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/mw45n5je_Logo%20Calvi%20Fishing.png";

export const Header = () => {
  const { t, language, changeLanguage } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/trips', label: t('nav.trips') },
    { path: '/fish-species', label: language === 'fr' ? 'Poissons' : language === 'it' ? 'Pesci' : 'Fish' },
    { path: '/gallery', label: t('nav.gallery') },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact', label: t('nav.contact') },
  ];

  const languages = [
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#b7ede0]/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img 
              src={LOGO_URL} 
              alt="Calvi Fishing" 
              className="h-12 w-12 sm:h-16 sm:w-16 object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm font-medium transition-colors ${
                  isActive(link.path) 
                    ? 'text-[#000000] font-semibold' 
                    : 'text-[#4B5563] hover:text-[#000000]'
                }`}
                data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-4">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 text-[#000000]" data-testid="language-selector">
                  <Globe size={18} />
                  <span className="hidden sm:inline uppercase">{language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={language === lang.code ? 'bg-[#b7ede0]' : ''}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Book CTA */}
            <Link to="/trips" className="hidden sm:block">
              <Button 
                className="bg-[#000000] hover:bg-[#1a1a1a] text-[#b7ede0] rounded-full px-6 font-semibold"
                data-testid="header-book-btn"
              >
                {t('nav.book')}
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-[#000000]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-[#b7ede0]">
          <nav className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-[#b7ede0] text-[#000000]'
                    : 'text-[#4B5563] hover:bg-[#b7ede0]/20'
                }`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-2">
              <Link to="/trips" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-[#000000] hover:bg-[#1a1a1a] text-[#b7ede0] rounded-full font-semibold">
                  {t('nav.book')}
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
